import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, FormControlLabel,
  InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import SsoService from '../../../shared/services/sso/sso.service';
import { IOrganisationSso, ISsoProvider } from '../../../shared/services/sso/sso.model';

// The single-sign-on configuration form for the caller's own organisation. Shared
// by the company admin page and the partner Security page (the backend /user/sso
// endpoints always act on the caller's own org). `returnTo` is where a successful
// Test sign-in lands.
export const OrganisationSsoSettings = ({ returnTo }: { returnTo: string }) => {
  const ssoService = useMemo(() => new SsoService(), []);
  const userNotifications = useContext(UserNotificationsContext);
  const userDetailsContext = useContext(UserDetailsContext);

  const [data, setData] = useState<IOrganisationSso | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenantIdInput, setTenantIdInput] = useState('');
  const [editingTenant, setEditingTenant] = useState(false);

  const load = () => ssoService.getOrganisationSso()
    .then((d) => { setData(d); setTenantIdInput(d.entraTenantId); })
    .catch(() => { /* the http interceptor handles auth errors */ })
    .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorToast = (err?: any) => userNotifications.openSnackbar({
    open: true,
    message: err?.response?.data?.message || 'There was an error, please try again',
    backgroundColor: 'text.primary',
  });

  // Every change persists immediately: send the full state with the changed
  // field overlaid.
  const persist = async (patch: Partial<Pick<IOrganisationSso, 'provider' | 'enabled' | 'enforceSso' | 'entraTenantId'>>) => {
    if (!data) return undefined;
    setSaving(true);
    try {
      const updated = await ssoService.updateOrganisationSso({
        provider: patch.provider ?? data.provider,
        enabled: patch.enabled ?? data.enabled,
        enforceSso: patch.enforceSso ?? data.enforceSso,
        entraTenantId: patch.entraTenantId ?? data.entraTenantId,
      });
      setData(updated);
      setTenantIdInput(updated.entraTenantId);
      return updated;
    } catch (err) {
      errorToast(err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveTenantId = async () => {
    try {
      await persist({ entraTenantId: tenantIdInput.trim() });
      setEditingTenant(false);
      userNotifications.openSnackbar({ open: true, message: 'Tenant ID saved' });
    } catch (err) { /* toast already shown */ }
  };

  const toggleEnabled = async (next: boolean) => {
    try {
      await persist(next ? { enabled: true } : { enabled: false, enforceSso: false });
      userNotifications.openSnackbar({ open: true, message: next ? 'Single sign-on turned on' : 'Single sign-on turned off' });
    } catch (err) { /* toast already shown */ }
  };

  const toggleEnforce = async (next: boolean) => {
    if (next) {
      const confirmed = await userNotifications.openDialog({
        active: true,
        title: 'Require single sign-on?',
        message: 'Everyone in your organisation will have to sign in through your identity provider, and password sign-in will be rejected. Make sure single sign-on works first with Test sign-in.',
        buttonCopy: 'Require it',
        cancelButtonCopy: 'Cancel',
      });
      if (!confirmed) return;
    }
    try {
      await persist({ enforceSso: next });
      userNotifications.openSnackbar({ open: true, message: next ? 'Single sign-on is now required' : 'Single sign-on is no longer required' });
    } catch (err) { /* toast already shown */ }
  };

  // Switching provider starts the new one from off, so an admin cannot leave one
  // provider enabled while setting another up.
  const changeProvider = async (next: ISsoProvider) => {
    if (!data || next === data.provider) return;
    try {
      await persist({ provider: next, enabled: false, enforceSso: false });
      userNotifications.openSnackbar({ open: true, message: `Provider set to ${next === 'GOOGLE' ? 'Google' : 'Microsoft Entra ID'}` });
    } catch (err) { /* toast already shown */ }
  };

  const testSignIn = async () => {
    const email = userDetailsContext.userDetails?.email;
    if (!email) return;
    try {
      const res = await ssoService.getAuthMethod(email, returnTo);
      if (res.method === 'sso' && res.ssoStartUrl) {
        window.open(res.ssoStartUrl, '_blank', 'noopener');
      } else {
        userNotifications.openSnackbar({ open: true, message: 'Enable single sign-on and save first, then test.', backgroundColor: 'text.primary' });
      }
    } catch (err) {
      errorToast(err);
    }
  };

  const hasSavedTenant = !!data?.entraTenantId;
  const tenantLocked = hasSavedTenant && !editingTenant;

  if (loading) {
    return <CircularProgress size={22} />;
  }
  if (!data) {
    return null;
  }

  const isGoogle = data.provider === 'GOOGLE';
  const providerWord = isGoogle ? 'Google' : 'Microsoft';
  // Entra needs a saved tenant id; Google only needs the platform credentials.
  const canEnable = isGoogle ? data.serverConfigured : hasSavedTenant;

  return (
    <Stack spacing={3} sx={{ maxWidth: 560 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Let your team sign in with their {providerWord} account.
      </Typography>

      {!data.serverConfigured && (
        <Alert severity="info">
          Single sign-on has not been set up on the platform yet. See docs/sso-setup.md or contact support before enabling it.
        </Alert>
      )}

      <FormControl fullWidth size="small" disabled={saving}>
        <InputLabel id="sso-provider-label">Provider</InputLabel>
        <Select
          labelId="sso-provider-label"
          label="Provider"
          value={data.provider}
          onChange={(e) => changeProvider(e.target.value as ISsoProvider)}
        >
          <MenuItem value="ENTRA">Microsoft Entra ID</MenuItem>
          <MenuItem value="GOOGLE">Google</MenuItem>
        </Select>
      </FormControl>

      {!isGoogle && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              label="Microsoft tenant ID"
              value={tenantIdInput}
              onChange={(e) => setTenantIdInput(e.target.value)}
              disabled={tenantLocked || saving}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            {tenantLocked ? (
              <Button variant="outlined" onClick={() => setEditingTenant(true)} sx={{ flexShrink: 0 }}>
                <Typography sx={{ px: 0.5 }}>Edit</Typography>
              </Button>
            ) : (
              <Button variant="contained" disabled={saving || !tenantIdInput.trim()} onClick={saveTenantId} sx={{ flexShrink: 0 }}>
                <Typography sx={{ px: 0.5 }}>Save</Typography>
              </Button>
            )}
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            Your organisation&apos;s directory (tenant) ID in Microsoft Entra.
          </Typography>
        </Box>
      )}

      <Box>
        <FormControlLabel
          control={<Switch checked={data.enabled} disabled={!canEnable || saving} onChange={(e) => toggleEnabled(e.target.checked)} />}
          label="Enable single sign-on"
        />
        {!canEnable && (
          <Typography variant="caption" sx={{ display: 'block', ml: '3px', color: 'text.secondary' }}>
            {isGoogle ? 'Google single sign-on is not set up on the platform yet.' : 'Save your tenant ID first.'}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 3, ml: '3px' }}>
          Turning single sign-on on lets your team sign in with {providerWord}. Leaving it on but not required is useful while you are testing, or if not everyone can use single sign-on yet, since those users can still sign in with a password.
        </Typography>
        <FormControlLabel
          control={<Switch checked={data.enforceSso} disabled={!data.enabled || saving} onChange={(e) => toggleEnforce(e.target.checked)} />}
          label="Require single sign-on (reject password sign-in)"
        />
      </Box>

      <Box>
        <Button variant="outlined" disabled={saving} onClick={testSignIn}>
          <Typography sx={{ px: 0.5 }}>Test sign-in</Typography>
        </Button>
      </Box>
    </Stack>
  );
};
